import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API } from '../App';

// ================= PERMISSIONS CONTEXT =================
// Provides role-based access control throughout the app

const PermissionsContext = createContext(null);

// Hook to access permissions
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    console.warn('usePermissions must be used within PermissionsProvider');
    // Return safe defaults if context not available
    return {
      permissions: {},
      userRole: null,
      loading: true,
      can: () => true, // Default to allowing if context not set up
      canAny: () => true,
      canAll: () => true,
    };
  }
  return context;
};

// Default permissions for each role (fallback if API fails)
const DEFAULT_ROLE_PERMISSIONS = {
  // Executive roles - full access
  owner: { _all: true },
  ceo: { _all: true },
  coo: { _all: true },
  president: { _all: true },
  vp: { _all: true },
  general_manager: { _all: true },
  admin: { _all: true },
  
  // Management roles - most access
  operations_manager: {
    view_dashboard: true, view_leads: true, view_calendar: true, view_field_visit: true,
    view_estimating: true, view_blueprint_takeoff: true, view_estimates: true, view_proposals: true,
    view_projects: true, view_subcontractors: true, view_walkthroughs: true, view_purchase_orders: true,
    view_time_tracking: true, view_job_costing: true, view_payments: true, view_documents: true,
    view_team: true, view_reports: true, view_settings: true, view_expenses: true,
    create_estimate: true, edit_estimate: true, delete_estimate: true,
    create_proposal: true, edit_proposal: true, send_proposal: true,
    submit_costs: true, approve_costs: true, generate_invoice: true, send_invoice: true,
    create_expense: true, edit_expense: true, delete_expense: true,
    manage_roles: false, manage_billing: false,
  },
  construction_manager: {
    view_dashboard: true, view_projects: true, view_subcontractors: true, view_walkthroughs: true,
    view_time_tracking: true, view_job_costing: true, view_calendar: true, view_field_visit: true,
    view_estimates: true, view_purchase_orders: true, view_team: true, view_reports: true,
    update_milestone_progress: true, add_project_milestones: true, assign_crew_to_project: true,
    submit_costs: true, approve_costs: true, schedule_field_visit: true,
  },
  pm: {
    view_dashboard: true, view_leads: true, view_calendar: true, view_field_visit: true,
    view_estimating: true, view_estimates: true, view_proposals: true, view_projects: true,
    view_subcontractors: true, view_walkthroughs: true, view_purchase_orders: true,
    view_time_tracking: true, view_job_costing: true, view_payments: true, view_documents: true,
    view_team: true, view_reports: true, view_settings: true,
    create_estimate: true, edit_estimate: true, create_proposal: true, send_proposal: true,
    update_milestone_progress: true, add_project_milestones: true, submit_costs: true,
    approve_costs: true, generate_invoice: true,
  },
  assistant_pm: {
    view_dashboard: true, view_projects: true, view_calendar: true, view_field_visit: true,
    view_estimates: true, view_walkthroughs: true, view_time_tracking: true, view_documents: true,
    update_milestone_progress: true, submit_costs: true, schedule_field_visit: true,
  },
  
  // Site leadership
  superintendent: {
    view_dashboard: true, view_projects: true, view_calendar: true, view_field_visit: true,
    view_subcontractors: true, view_time_tracking: true, view_walkthroughs: true,
    update_milestone_progress: true, submit_costs: true, approve_costs: true,
    schedule_field_visit: true, conduct_walkthrough: true, capture_field_photos: true,
    log_time_entries: true, approve_time_entries: true, assign_crew_to_project: true,
  },
  site_supervisor: {
    view_dashboard: true, view_projects: true, view_calendar: true, view_field_visit: true,
    view_time_tracking: true, update_milestone_progress: true, submit_costs: true,
    schedule_field_visit: true, conduct_walkthrough: true, capture_field_photos: true,
    log_time_entries: true,
  },
  foreman: {
    view_dashboard: true, view_projects: true, view_time_tracking: true,
    update_milestone_progress: true, submit_costs: true, capture_field_photos: true,
    log_time_entries: true,
  },
  lead_carpenter: {
    view_dashboard: true, view_projects: true, view_time_tracking: true,
    update_milestone_progress: true, capture_field_photos: true, log_time_entries: true,
  },
  
  // Finance roles
  cfo: {
    view_dashboard: true, view_job_costing: true, view_payments: true, view_reports: true,
    view_estimates: true, view_projects: true, view_settings: true,
    view_cost_breakdown: true, view_profit_margins: true, view_company_financials: true,
    approve_costs: true, generate_invoice: true, send_invoice: true, mark_payment_received: true,
    manage_payment_schedule: true, manage_billing: true,
  },
  controller: {
    view_dashboard: true, view_job_costing: true, view_payments: true, view_reports: true,
    view_cost_breakdown: true, view_profit_margins: true, view_company_financials: true,
    approve_costs: true, generate_invoice: true, send_invoice: true, mark_payment_received: true,
  },
  bookkeeper: {
    view_dashboard: true, view_job_costing: true, view_payments: true,
    view_cost_breakdown: true, submit_costs: true, mark_payment_received: true,
  },
  accountant: {
    view_dashboard: true, view_job_costing: true, view_payments: true, view_reports: true,
    view_cost_breakdown: true, view_profit_margins: true, generate_invoice: true,
  },
  ap_clerk: {
    view_dashboard: true, view_payments: true, view_purchase_orders: true,
    submit_costs: true, view_cost_breakdown: true,
  },
  ar_clerk: {
    view_dashboard: true, view_payments: true, generate_invoice: true, send_invoice: true,
    mark_payment_received: true,
  },
  payroll: {
    view_dashboard: true, view_time_tracking: true, view_team: true,
    view_all_time_entries: true, approve_time_entries: true,
  },
  
  // Office roles
  office_manager: {
    view_dashboard: true, view_leads: true, view_calendar: true, view_documents: true,
    view_team: true, view_settings: true, view_payments: true,
    create_lead: true, edit_lead: true, schedule_field_visit: true,
    add_team_members: true, edit_team_members: true,
  },
  office: {
    view_dashboard: true, view_leads: true, view_calendar: true, view_documents: true,
    create_lead: true, edit_lead: true, schedule_field_visit: true,
  },
  receptionist: {
    view_dashboard: true, view_leads: true, view_calendar: true,
    create_lead: true, schedule_field_visit: true,
  },
  admin_assistant: {
    view_dashboard: true, view_leads: true, view_calendar: true, view_documents: true,
    create_lead: true, edit_lead: true, schedule_field_visit: true,
  },
  
  // Sales roles
  sales_manager: {
    view_dashboard: true, view_leads: true, view_estimates: true, view_proposals: true,
    view_calendar: true, view_reports: true,
    create_lead: true, edit_lead: true, convert_lead_to_client: true,
    create_estimate: true, edit_estimate: true, create_proposal: true, send_proposal: true,
    view_proposal_analytics: true,
  },
  sales_rep: {
    view_dashboard: true, view_leads: true, view_calendar: true,
    create_lead: true, edit_lead: true, schedule_field_visit: true,
  },
  business_dev: {
    view_dashboard: true, view_leads: true, view_proposals: true, view_reports: true,
    create_lead: true, edit_lead: true, view_proposal_analytics: true,
  },
  
  // Estimating roles
  estimator: {
    view_dashboard: true, view_estimating: true, view_blueprint_takeoff: true,
    view_estimates: true, view_proposals: true, view_leads: true,
    create_estimate: true, edit_estimate: true, duplicate_estimate: true,
    use_ai_estimating: true, use_ai_takeoff: true, export_estimate: true,
    create_proposal: true, edit_proposal: true,
  },
  senior_estimator: {
    view_dashboard: true, view_estimating: true, view_blueprint_takeoff: true,
    view_estimates: true, view_proposals: true, view_leads: true, view_job_costing: true,
    create_estimate: true, edit_estimate: true, duplicate_estimate: true, delete_estimate: true,
    use_ai_estimating: true, use_ai_takeoff: true, use_ai_estimating_rules: true,
    export_estimate: true, edit_estimate_pricing: true,
    create_proposal: true, edit_proposal: true, send_proposal: true,
  },
  bid_manager: {
    view_dashboard: true, view_estimates: true, view_proposals: true, view_reports: true,
    create_estimate: true, edit_estimate: true, edit_estimate_pricing: true,
    create_proposal: true, send_proposal: true, view_proposal_analytics: true,
  },
  
  // Field roles
  field: {
    view_dashboard: true, view_projects: true, view_time_tracking: true,
    update_milestone_progress: true, capture_field_photos: true, log_time_entries: true,
    submit_costs: true,
  },
  journeyman: {
    view_dashboard: true, view_projects: true, view_time_tracking: true,
    update_milestone_progress: true, capture_field_photos: true, log_time_entries: true,
  },
  apprentice: {
    view_dashboard: true, view_projects: true, view_time_tracking: true,
    capture_field_photos: true, log_time_entries: true,
  },
  laborer: {
    view_dashboard: true, view_projects: true, view_time_tracking: true,
    log_time_entries: true,
  },
  helper: {
    view_dashboard: true, view_time_tracking: true, log_time_entries: true,
  },
  
  // Safety & QC roles
  safety_manager: {
    view_dashboard: true, view_projects: true, view_walkthroughs: true, view_reports: true,
    view_field_visit: true, schedule_field_visit: true, conduct_walkthrough: true,
    capture_field_photos: true, submit_field_reports: true,
  },
  safety_officer: {
    view_dashboard: true, view_projects: true, view_walkthroughs: true, view_field_visit: true,
    conduct_walkthrough: true, capture_field_photos: true, submit_field_reports: true,
  },
  qc_manager: {
    view_dashboard: true, view_projects: true, view_walkthroughs: true, view_reports: true,
    conduct_walkthrough: true, capture_field_photos: true, submit_field_reports: true,
    approve_sub_work: true,
  },
  qc_inspector: {
    view_dashboard: true, view_projects: true, view_walkthroughs: true,
    conduct_walkthrough: true, capture_field_photos: true, submit_field_reports: true,
  },
  
  // Purchasing & Warehouse
  purchasing_manager: {
    view_dashboard: true, view_purchase_orders: true, view_subcontractors: true,
    create_purchase_order: true, edit_purchase_order: true, approve_purchase_order: true,
    delete_purchase_order: true, view_supplier_pricing: true, receive_materials: true,
  },
  purchasing_agent: {
    view_dashboard: true, view_purchase_orders: true,
    create_purchase_order: true, edit_purchase_order: true, view_supplier_pricing: true,
  },
  warehouse_manager: {
    view_dashboard: true, view_purchase_orders: true,
    receive_materials: true, view_supplier_pricing: true,
  },
  warehouse_staff: {
    view_dashboard: true, view_purchase_orders: true, receive_materials: true,
  },
  equipment_manager: {
    view_dashboard: true, view_purchase_orders: true, view_time_tracking: true,
    create_purchase_order: true, receive_materials: true,
  },
  
  // HR roles
  hr_manager: {
    view_dashboard: true, view_team: true, view_time_tracking: true, view_settings: true,
    add_team_members: true, edit_team_members: true, remove_team_members: true,
    view_all_time_entries: true, approve_time_entries: true,
  },
  hr_coordinator: {
    view_dashboard: true, view_team: true, view_time_tracking: true,
    add_team_members: true, edit_team_members: true, view_all_time_entries: true,
  },
  recruiter: {
    view_dashboard: true, view_team: true, add_team_members: true,
  },
  training_coordinator: {
    view_dashboard: true, view_team: true, view_time_tracking: true,
  },
  
  // IT & Marketing
  it_admin: { _all: true }, // IT admin gets full access for system management
  marketing_manager: {
    view_dashboard: true, view_leads: true, view_proposals: true, view_reports: true,
    view_proposal_analytics: true,
  },
  
  // External/Limited roles
  client_viewer: {
    view_dashboard: true,
  },
  subcontractor: {
    view_dashboard: true, submit_costs: true, log_time_entries: true, capture_field_photos: true,
  },
  vendor: {
    view_dashboard: true, view_purchase_orders: true,
  },
  consultant: {
    view_dashboard: true, view_projects: true, view_estimates: true, view_reports: true,
  },
};

// Permission groups for navigation items
export const NAV_PERMISSION_MAP = {
  '/dashboard': 'view_dashboard',
  '/leads': 'view_leads',
  '/appointments': 'view_calendar',
  '/calendar/field-visit': 'view_field_visit',
  '/estimating': 'view_estimating',
  '/blueprint-takeoff': 'view_blueprint_takeoff',
  '/estimates': 'view_estimates',
  '/proposals': 'view_proposals',
  '/projects': 'view_projects',
  '/subcontractors': 'view_subcontractors',
  '/walkthroughs': 'view_walkthroughs',
  '/purchase-orders': 'view_purchase_orders',
  '/time-tracking': 'view_time_tracking',
  '/job-costing': 'view_job_costing',
  '/payments': 'view_payments',
  '/expenses': 'view_expenses',
  '/documents': 'view_documents',
  '/team': 'view_team',
  '/reports': 'view_reports',
  '/settings': 'view_settings',
};

// Permissions Provider Component
export const PermissionsProvider = ({ children, user }) => {
  const [permissions, setPermissions] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Get user's role - defaults to 'field' if not set
  const userRole = user?.role || 'field';

  // Load permissions from API or use defaults
  useEffect(() => {
    const loadPermissions = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Try to load custom permissions from API
        const response = await API.get('/settings/permissions');
        if (response.data && response.data[userRole]) {
          setRolePermissions(response.data);
          setPermissions(response.data[userRole]);
        } else {
          // Use default permissions for role
          setPermissions(DEFAULT_ROLE_PERMISSIONS[userRole] || {});
        }
      } catch (error) {
        console.log('Using default permissions for role:', userRole);
        setPermissions(DEFAULT_ROLE_PERMISSIONS[userRole] || {});
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, [user, userRole]);

  // Check if user has a specific permission
  const can = useCallback((permission) => {
    if (!user) return false;
    
    // Admin/Owner roles with _all flag get everything
    if (permissions._all) return true;
    
    // Check specific permission
    return permissions[permission] === true;
  }, [user, permissions]);

  // Check if user has ANY of the given permissions
  const canAny = useCallback((permissionList) => {
    if (!user) return false;
    if (permissions._all) return true;
    return permissionList.some(p => permissions[p] === true);
  }, [user, permissions]);

  // Check if user has ALL of the given permissions
  const canAll = useCallback((permissionList) => {
    if (!user) return false;
    if (permissions._all) return true;
    return permissionList.every(p => permissions[p] === true);
  }, [user, permissions]);

  // Check if user can access a navigation path
  const canAccessPath = useCallback((path) => {
    if (!user) return false;
    if (permissions._all) return true;
    
    const permission = NAV_PERMISSION_MAP[path];
    if (!permission) return true; // Allow if no permission mapping
    
    return permissions[permission] === true;
  }, [user, permissions]);

  // Get all permissions for current user
  const getAllPermissions = useCallback(() => {
    return { ...permissions };
  }, [permissions]);

  // Check if user is admin (owner, admin, or has _all flag)
  const isAdmin = useCallback(() => {
    return ['owner', 'admin', 'ceo', 'it_admin'].includes(userRole) || permissions._all;
  }, [userRole, permissions]);

  const value = {
    permissions,
    rolePermissions,
    userRole,
    loading,
    can,
    canAny,
    canAll,
    canAccessPath,
    getAllPermissions,
    isAdmin,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

// Higher-Order Component for permission-protected components
export const withPermission = (WrappedComponent, requiredPermission) => {
  return function PermissionProtectedComponent(props) {
    const { can, loading } = usePermissions();
    
    if (loading) {
      return null; // Or a loading spinner
    }
    
    if (!can(requiredPermission)) {
      return null; // Or an access denied message
    }
    
    return <WrappedComponent {...props} />;
  };
};

// Component that conditionally renders children based on permission
export const CanAccess = ({ permission, permissions, any, all, children, fallback = null }) => {
  const { can, canAny, canAll, loading } = usePermissions();
  
  if (loading) return null;
  
  // Check single permission
  if (permission && can(permission)) {
    return children;
  }
  
  // Check any of multiple permissions
  if (permissions && any && canAny(permissions)) {
    return children;
  }
  
  // Check all of multiple permissions
  if (permissions && all && canAll(permissions)) {
    return children;
  }
  
  // Single permission check (backwards compatibility)
  if (permission && !can(permission)) {
    return fallback;
  }
  
  return fallback;
};

export default PermissionsContext;
